import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalContent, Button } from '@clearcover/atlas';
import { GrantsType, hasScopedPermission } from 'Shared/utils';
import styles from './styles.module.css';

export const FormModalErrorMessage: React.FC = ({ children }) => (
  <div>{children}</div>
);

export type FormModalUnderWritingErrorsProps = {
  errors: Array<string>;
  grants?: GrantsType;
  submitIgnoringUnderwriting?: () => void | Promise<void>;
};

export const FormModalUnderWritingErrors: React.FC<FormModalUnderWritingErrorsProps> = ({
  errors,
  grants,
  submitIgnoringUnderwriting,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [
    temporarilyIgnoreSubmitting,
    setTemporarilyIgnoreSubmitting,
  ] = useState(false);

  const hasBypassUwPermission =
    grants &&
    hasScopedPermission(
      grants,
      'partner',
      'policies.drafts.updates_bypass_underwriting'
    );

  useEffect(() => {
    if (errors.length > 0) {
      setIsOpen(true);
    }
  }, [errors]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      controlled={true}
      open={isOpen}
      handleClose={() => {
        setIsOpen(false);
      }}
      data-testid="driver-form-undefined-status-modal"
    >
      {closeModal => {
        return (
          <>
            <ModalHeader>
              <h3 className={styles.modalHeader}>
                The requested policy change cannot be processed
              </h3>
            </ModalHeader>
            <ModalContent>
              <div className={styles.modalBody}>
                <p>
                  The following issue(s) occured while processing the policy
                  change:
                  <ul>
                    {errors.map((err, index) => (
                      <li key={index}>
                        <FormModalErrorMessage>{err}</FormModalErrorMessage>
                      </li>
                    ))}
                  </ul>
                </p>
                <p>
                  Please return to the policy change screen and resolve the
                  issues before continuing.
                </p>
                <div className={styles.modalButtons}>
                  {hasBypassUwPermission && submitIgnoringUnderwriting && (
                    <Button
                      data-testid="temporarily-ignore"
                      variant="danger-secondary"
                      size="md"
                      isSubmitting={temporarilyIgnoreSubmitting}
                      onClick={async () => {
                        setTemporarilyIgnoreSubmitting(true);
                        try {
                          await submitIgnoringUnderwriting();
                          closeModal();
                        } finally {
                          setTemporarilyIgnoreSubmitting(false);
                        }
                      }}
                    >
                      Temporarily ignore
                    </Button>
                  )}
                  <Button
                    data-testid="return-button"
                    variant="primary"
                    size="md"
                    isSubmitting={false}
                    onClick={closeModal}
                  >
                    Return to editing
                  </Button>
                </div>
              </div>
            </ModalContent>
          </>
        );
      }}
    </Modal>
  );
};
